import {useCallback, useEffect, useState} from 'react'
import {backendGetRequest, backendRequest} from '../../../../../common/utils/requests'

export type PendingPammOffer = {
  managerId: number
  offerId: number
  name: string
  managerName: string
  currency: string
  minInitialInvestment: number
  performanceFeeTopLevel: number
}

const fetchPendingOffers = async (): Promise<PendingPammOffer[]> => {
  const response = await backendGetRequest('/api/v2/dashboard/pamm/investors/offers/pending', true)
  if (!response.ok) {
    throw new Error(`Failed to fetch pending PAMM offers ${response.status} ${response.statusText}`)
  }
  const data = await response.json()
  return data.pendingOffers ?? []
}

/**
 * Drives the dashboard's "Pending PAMM Offers" floating block (PT-5354).
 * Provides the list plus a `dismiss` callback that hits the dismiss endpoint
 * and optimistically removes the offer locally so the block hides
 * immediately when the last offer is dismissed.
 */
export const usePendingPammOffers = () => {
  const [pendingOffers, setPendingOffers] = useState<PendingPammOffer[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    fetchPendingOffers()
      .then((offers) => {
        if (!cancelled) setPendingOffers(offers)
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching pending PAMM offers:', error)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const dismiss = useCallback(async ({managerId, offerId}: {managerId: number; offerId: number}) => {
    setPendingOffers((current) =>
      current.filter((offer) => offer.managerId !== managerId || offer.offerId !== offerId),
    )
    try {
      await backendRequest(`/api/v2/dashboard/pamm/managers/${managerId}/offers/${offerId}/dismiss`, {})
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to dismiss PAMM offer', error)
    }
  }, [])

  return {dismiss, loading, pendingOffers}
}
